<template>
  <div :class="[$attrs.class]">
    <h3 class="tw-font-black tw-text-4xl" v-if="layout == 'summary'">
      {{ group }}<template v-if="parentOptions"> </template>
      <br />
      <br />
    </h3>
    <template v-if="layout != 'summary' || (layout == 'summary' && parentOptions)">
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8">
        <div class="tw-flex tw-flex-col tw-gap-4">
          <h3 class="tw-text-3xl tw-mb-2 tw-font-bold tw-normal-case">
            {{ baseTitle }}
          </h3>
          <div class="tw-text-2xl tw-mb-2">
            <template v-if="baseHasAllergens">
              Contains <strong>{{ baseAllergenPhrase }}</strong> and is <strong>{{ baseDietBody }}</strong>.
            </template>
            <template v-else>
              Is <strong>{{ baseDietStandaloneBody }}</strong>.
            </template>
          </div>
        </div>
        <div v-if="sideItemsPresent" class="tw-flex tw-flex-col tw-gap-4">
          <h3 class="tw-text-3xl tw-mb-2 tw-font-bold tw-normal-case">
            {{ sideTitle }}
          </h3>
          <div class="tw-text-2xl tw-mb-2">
            Is provided on the side
            <template v-if="sideHasAllergens">
              and contains <strong>{{ sideAllergenPhrase }}</strong> and is <strong>{{ sideDietBody }}</strong>.
            </template>
            <template v-else>
              and is <strong>{{ sideDietBody }}</strong>.
            </template>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      Make a selection
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'

  const props = defineProps<{ 
    allergens: string[], 
    allDiets: string[],
    diets: string[],
    group: string,
    parentOptions: string,
    layout: string,
    baseAllergens?: string[],
    sideAllergens?: string[],
    baseDiets?: string[],
    sideDiets?: string[],
    baseNames?: string[],
    sideNames?: string[],
    hasSideItems?: boolean,
  }>();

  const baseAllergensList = computed(() => props.baseAllergens ?? []);
  const sideAllergensList = computed(() => props.sideAllergens ?? []);
  const baseDietsList = computed(() => props.baseDiets ?? []);
  const sideDietsList = computed(() => props.sideDiets ?? []);
  const baseNameList = computed(() => normalizeList(props.baseNames ?? []));
  const sideNameList = computed(() => normalizeList(props.sideNames ?? []));
  const sideItemsPresent = computed(() => {
    if (props.hasSideItems === true) {
      return true;
    }

    return sideAllergensList.value.length > 0 || sideDietsList.value.length > 0;
  });
  const capitalizeFirst = (phrase: string) =>
    phrase.length > 0 ? phrase[0].toUpperCase() + phrase.slice(1) : phrase;
  const normalizeList = (list: string[] = []) =>
    Array.from(
      new Set(
        list
          .filter((item) => typeof item === 'string')
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
      )
    );

  const describeAllergens = (list: string[]) => {
    const normalized = normalizeList(list);
    return normalized.length > 0 ? normalized.join(', ') : 'no listed allergens';
  };

  const describeDiets = (list: string[]) => {
    const normalized = normalizeList(list);
    return normalized.length > 0 ? normalized.join(', ') : 'NOT vegetarian or vegan';
  };

  const baseTitle = computed(() => (baseNameList.value.length > 0 ? baseNameList.value.join(', ') : 'Base Item'));
  const sideTitle = computed(() => (sideNameList.value.length > 0 ? sideNameList.value.join(', ') : 'Side Item'));

  const baseAllergenSource = computed(() =>
    baseAllergensList.value.length > 0 ? baseAllergensList.value : (props.allergens ?? [])
  );
  const baseDietSource = computed(() =>
    baseDietsList.value.length > 0 ? baseDietsList.value : (props.diets ?? [])
  );
  const sideAllergenSource = computed(() =>
    sideAllergensList.value.length > 0 ? sideAllergensList.value : []
  );
  const sideDietSource = computed(() =>
    sideDietsList.value.length > 0 ? sideDietsList.value : []
  );

  const baseHasAllergens = computed(() => normalizeList(baseAllergenSource.value).length > 0);
  const sideHasAllergens = computed(() => normalizeList(sideAllergenSource.value).length > 0);

  const baseAllergenPhrase = computed(() => describeAllergens(baseAllergenSource.value));
  const baseDietBody = computed(() => describeDiets(baseDietSource.value));
  const baseDietStandaloneBody = computed(() => capitalizeFirst(baseDietBody.value));

  const sideAllergenPhrase = computed(() => describeAllergens(sideAllergenSource.value));
  const sideDietBody = computed(() => describeDiets(sideDietSource.value));

</script>